/**
 * Small, safe markdown renderer for agent output and report sections. Builds React elements only — no
 * HTML injection. Supports headings, paragraphs (soft breaks kept), **bold**, *italic*, ~~strike~~,
 * `code`, fenced code blocks, ordered/unordered (nested) lists, task items, blockquotes, tables, rules,
 * links (http/https/mailto/relative only) and bare-URL autolinks. `trailing` (e.g. a streaming caret)
 * is rendered inline at the end of the last block.
 */
import { Check, Copy } from "lucide-react";
import { Fragment, memo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/cn";
import { safeInAppHref, safeMediaHref } from "@/lib/safe-url";

// ─── Block model ───────────────────────────────────────────────────────────

type ListItem = { text: string; checked?: boolean; children: Block[] };
type Block =
  | { t: "heading"; level: number; text: string }
  | { t: "para"; text: string }
  | { t: "code"; lang: string; code: string }
  | { t: "list"; ordered: boolean; start: number; items: ListItem[] }
  | { t: "quote"; blocks: Block[] }
  | { t: "table"; head: string[]; align: ("left" | "center" | "right" | null)[]; rows: string[][] }
  | { t: "hr" };

const LIST_RE = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const FENCE_RE = /^\s*(```|~~~)\s*([\w+-]*)\s*$/;
const HEADING_RE = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const HR_RE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = "";
  let inCode = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === "`") inCode = !inCode;
    if (ch === "\\" && s[i + 1] === "|") {
      cur += "|";
      i++;
      continue;
    }
    if (ch === "|" && !inCode) {
      cells.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

function isBlockStart(line: string, next: string | undefined): boolean {
  return FENCE_RE.test(line) || HEADING_RE.test(line) || HR_RE.test(line) || LIST_RE.test(line) || /^\s*>/.test(line) || (line.includes("|") && next !== undefined && TABLE_SEP_RE.test(next));
}

export function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, "\n").replace(/\t/g, "    ").split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const fence = FENCE_RE.exec(line);
    if (fence) {
      const marker = fence[1]!;
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trim().startsWith(marker)) code.push(lines[i++]!);
      i++; // closing fence (or EOF while streaming)
      out.push({ t: "code", lang: fence[2] ?? "", code: code.join("\n") });
      continue;
    }
    const h = HEADING_RE.exec(line);
    if (h) {
      out.push({ t: "heading", level: h[1]!.length, text: h[2]! });
      i++;
      continue;
    }
    if (HR_RE.test(line)) {
      out.push({ t: "hr" });
      i++;
      continue;
    }
    if (line.includes("|") && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]!)) {
      const head = splitRow(line);
      const align = splitRow(lines[i + 1]!).map((c) => (c.startsWith(":") && c.endsWith(":") ? "center" : c.endsWith(":") ? "right" : c.startsWith(":") ? "left" : null));
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.trim() && lines[i]!.includes("|")) rows.push(splitRow(lines[i++]!));
      out.push({ t: "table", head, align, rows });
      continue;
    }
    if (/^\s*>/.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && lines[i]!.trim() && (/^\s*>/.test(lines[i]!) || !isBlockStart(lines[i]!, lines[i + 1]))) {
        inner.push(lines[i]!.replace(/^\s*>\s?/, ""));
        i++;
      }
      out.push({ t: "quote", blocks: parseBlocks(inner.join("\n")) });
      continue;
    }
    const lm = LIST_RE.exec(line);
    if (lm) {
      const baseIndent = lm[1]!.length;
      const ordered = /\d/.test(lm[2]!);
      const items: ListItem[] = [];
      while (i < lines.length) {
        const cur = lines[i]!;
        const m = LIST_RE.exec(cur);
        if (m && m[1]!.length <= baseIndent + 1 && /\d/.test(m[2]!) === ordered) {
          let text = m[3]!;
          let checked: boolean | undefined;
          const task = /^\[([ xX])\]\s+(.*)$/.exec(text);
          if (task) {
            checked = task[1] !== " ";
            text = task[2]!;
          }
          const nested: string[] = [];
          i++;
          while (i < lines.length) {
            const n = lines[i]!;
            if (!n.trim()) {
              // blank line: continue the list only if the next line is indented / another item
              const peek = lines[i + 1];
              if (peek !== undefined && (/^\s{2,}\S/.test(peek) || (LIST_RE.exec(peek)?.[1]!.length ?? -1) >= 0)) {
                nested.push("");
                i++;
                continue;
              }
              break;
            }
            const nm = LIST_RE.exec(n);
            const indent = n.length - n.trimStart().length;
            if (nm && nm[1]!.length <= baseIndent + 1) break;
            if (indent > baseIndent) nested.push(n.slice(Math.min(indent, baseIndent + 2)));
            else if (!isBlockStart(n, lines[i + 1])) text += `\n${n.trim()}`;
            else break;
            i++;
          }
          while (nested.length && !nested[nested.length - 1]!.trim()) nested.pop();
          items.push({ text, checked, children: nested.length ? parseBlocks(nested.join("\n")) : [] });
          continue;
        }
        if (!cur.trim()) {
          const peek = lines[i + 1];
          const pm = peek !== undefined ? LIST_RE.exec(peek) : null;
          if (pm && pm[1]!.length <= baseIndent + 1 && /\d/.test(pm[2]!) === ordered) {
            i++;
            continue;
          }
        }
        break;
      }
      out.push({ t: "list", ordered, start: ordered ? parseInt(lm[2]!, 10) || 1 : 1, items });
      continue;
    }
    const para: string[] = [line.trim()];
    i++;
    while (i < lines.length && lines[i]!.trim() && !isBlockStart(lines[i]!, lines[i + 1])) para.push(lines[i++]!.trim());
    out.push({ t: "para", text: para.join("\n") });
  }
  return out;
}

// ─── Inline ────────────────────────────────────────────────────────────────

export function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (href.startsWith("#") && !/[\s"'<>]/.test(href)) return href;
  if (/^mailto:[^\s"'<>]+$/i.test(href)) return href;
  // In-app (same-origin) paths, else external http(s) — never javascript:/data:/protocol-relative.
  return safeInAppHref(href) ?? safeMediaHref(href) ?? null;
}

type InlineRule = { re: RegExp; render: (m: RegExpExecArray, key: string, ctx: InlineCtx) => ReactNode };
interface InlineCtx {
  linkClass?: string;
}

const RULES: InlineRule[] = [
  { re: /`([^`\n]+)`/, render: (m, key) => <code key={key} className="rounded-[5px] border border-line bg-surface-2 px-1 py-px font-mono text-[0.86em] text-fg/90">{m[1]}</code> },
  {
    re: /\[([^\]\n]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/,
    render: (m, key, ctx) => {
      const href = safeHref(m[2]!);
      const children = inline(m[1]!, `${key}l`, ctx);
      if (!href) return <span key={key}>{children}</span>;
      return <SmartLink key={key} href={href} className={ctx.linkClass}>{children}</SmartLink>;
    },
  },
  { re: /\*\*(?=\S)([\s\S]+?)(?<=\S)\*\*/, render: (m, key, ctx) => <strong key={key} className="font-semibold text-fg">{inline(m[1]!, `${key}b`, ctx)}</strong> },
  { re: /__(?=\S)([\s\S]+?)(?<=\S)__(?!\w)/, render: (m, key, ctx) => <strong key={key} className="font-semibold text-fg">{inline(m[1]!, `${key}b`, ctx)}</strong> },
  { re: /~~(?=\S)([\s\S]+?)(?<=\S)~~/, render: (m, key, ctx) => <del key={key} className="text-muted">{inline(m[1]!, `${key}s`, ctx)}</del> },
  { re: /(?<![*\w])\*(?=[^\s*])([^*\n]+?)(?<=\S)\*(?!\*)/, render: (m, key, ctx) => <em key={key}>{inline(m[1]!, `${key}i`, ctx)}</em> },
  { re: /(?<![_\w])_(?=[^\s_])([^_\n]+?)(?<=\S)_(?![_\w])/, render: (m, key, ctx) => <em key={key}>{inline(m[1]!, `${key}i`, ctx)}</em> },
  {
    re: /\bhttps?:\/\/[^\s<>()[\]]+[^\s<>()[\].,;:!?'"]/,
    render: (m, key, ctx) => (
      <SmartLink key={key} href={m[0]} className={cn("break-all", ctx.linkClass)}>
        {m[0]}
      </SmartLink>
    ),
  },
];

function SmartLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  const cls = cn("text-link underline decoration-link/40 underline-offset-2 hover:text-lavender hover:decoration-lavender", className);
  const inApp = safeInAppHref(href);
  if (inApp) {
    return (
      <Link to={inApp} className={cls}>
        {children}
      </Link>
    );
  }
  const external = href.startsWith("#") || /^mailto:/i.test(href) ? href : safeMediaHref(href);
  if (!external) return <span className={className}>{children}</span>;
  return (
    <a href={external} target="_blank" rel="noopener noreferrer" className={cls}>
      {children}
    </a>
  );
}

function withBreaks(text: string, key: string): ReactNode[] {
  const parts = text.split("\n");
  return parts.flatMap((p, i) => (i === 0 ? [p] : [<br key={`${key}br${i}`} />, p]));
}

export function inline(text: string, keyPrefix = "i", ctx: InlineCtx = {}): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest) {
    let best: { m: RegExpExecArray; rule: InlineRule } | null = null;
    for (const rule of RULES) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.m.index)) best = { m, rule };
    }
    if (!best) {
      out.push(...withBreaks(rest.replace(/\\([\\`*_{}[\]()#+\-.!|~])/g, "$1"), `${keyPrefix}t${n}`));
      break;
    }
    if (best.m.index > 0) out.push(...withBreaks(rest.slice(0, best.m.index).replace(/\\([\\`*_{}[\]()#+\-.!|~])/g, "$1"), `${keyPrefix}t${n}`));
    out.push(best.rule.render(best.m, `${keyPrefix}${n}`, ctx));
    rest = rest.slice(best.m.index + best.m[0].length);
    n++;
  }
  return out;
}

// ─── Render ────────────────────────────────────────────────────────────────

function CodeBlock({ code, lang }: { code: string; lang: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="group/code relative my-3 overflow-hidden rounded-xl border border-line bg-surface-3">
      <div className="flex items-center justify-between border-b border-line/70 px-3 py-1.5 text-[11px] text-subtle">
        <span className="font-mono">{lang || "code"}</span>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(code).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1400);
            });
          }}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-muted hover:bg-hover hover:text-fg"
          aria-label="Copy code"
        >
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto px-3.5 py-3 font-mono text-[12.5px] leading-relaxed text-fg/90 scrollbar-thin">
        <code>{code}</code>
      </pre>
    </div>
  );
}

const H_CLASS: Record<number, string> = {
  1: "mt-5 mb-2 text-[1.45em] font-semibold tracking-[-0.015em]",
  2: "mt-5 mb-2 text-[1.25em] font-semibold tracking-[-0.01em]",
  3: "mt-4 mb-1.5 text-[1.1em] font-semibold",
  4: "mt-3 mb-1 font-semibold",
  5: "mt-3 mb-1 font-semibold text-muted",
  6: "mt-3 mb-1 text-[0.9em] font-semibold uppercase tracking-wide text-muted",
};

function renderBlocks(blocks: Block[], key: string, trailing: ReactNode, ctx: InlineCtx): ReactNode[] {
  return blocks.map((b, idx) => {
    const k = `${key}-${idx}`;
    const tail = idx === blocks.length - 1 ? trailing : null;
    switch (b.t) {
      case "heading": {
        const Tag = `h${Math.min(6, b.level + 1)}` as "h2";
        return (
          <Tag key={k} className={cn(H_CLASS[b.level], "first:mt-0")}>
            {inline(b.text, k, ctx)}
            {tail}
          </Tag>
        );
      }
      case "para":
        return (
          <p key={k} className="my-2.5 first:mt-0 last:mb-0">
            {inline(b.text, k, ctx)}
            {tail}
          </p>
        );
      case "code":
        return (
          <Fragment key={k}>
            <CodeBlock code={b.code} lang={b.lang} />
            {tail}
          </Fragment>
        );
      case "hr":
        return <hr key={k} className="my-4 border-line" />;
      case "quote":
        return (
          <blockquote key={k} className="my-3 border-l-2 border-line-strong pl-3.5 text-muted">
            {renderBlocks(b.blocks, k, tail, ctx)}
          </blockquote>
        );
      case "list": {
        const ListTag = b.ordered ? "ol" : "ul";
        return (
          <ListTag key={k} start={b.ordered && b.start !== 1 ? b.start : undefined} className={cn("my-2.5 space-y-1 pl-5 first:mt-0 last:mb-0", b.ordered ? "list-decimal marker:text-muted" : "list-disc marker:text-subtle")}>
            {b.items.map((it, j) => {
              const last = j === b.items.length - 1;
              return (
                <li key={`${k}-${j}`} className={cn("pl-1", it.checked !== undefined && "-ml-5 list-none")}>
                  {it.checked !== undefined && (
                    <span className={cn("mr-2 inline-grid size-3.5 translate-y-[2px] place-items-center rounded-[4px] border", it.checked ? "border-primary bg-primary text-white" : "border-line-strong")} aria-hidden>
                      {it.checked && <Check className="size-2.5" strokeWidth={3} />}
                    </span>
                  )}
                  {inline(it.text, `${k}-${j}`, ctx)}
                  {last && !it.children.length ? tail : null}
                  {it.children.length > 0 && renderBlocks(it.children, `${k}-${j}c`, last ? tail : null, ctx)}
                </li>
              );
            })}
          </ListTag>
        );
      }
      case "table": {
        // PlayAd shot-plan look: soft panel, header strip, no grid lines; a leading Time/Shot column stays narrow + muted.
        const narrowFirst = /^(time|timing|timestamp|t|sec|secs|seconds|shot|scene|#|no\.?|step)$/i.test((b.head[0] ?? "").replace(/[*_`]/g, "").trim());
        return (
          <Fragment key={k}>
            <div className="my-4 overflow-x-auto rounded-xl bg-[#232325] scrollbar-thin">
              <table className="w-full border-collapse text-[0.94em]">
                <thead className="bg-[#2a2a2c]">
                  <tr>
                    {b.head.map((h, j) => (
                      <th
                        key={j}
                        className={cn("px-4 py-3 text-left font-medium text-fg/90", j === 0 && narrowFirst && "w-[1%] whitespace-nowrap")}
                        style={{ textAlign: b.align[j] ?? undefined }}
                      >
                        {inline(h, `${k}h${j}`, ctx)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((r, ri) => (
                    <tr key={ri} className={cn(!narrowFirst && "border-t border-line/50")}>
                      {b.head.map((_, j) => (
                        <td
                          key={j}
                          className={cn("px-4 py-3 align-middle leading-relaxed tnum", j === 0 && narrowFirst ? "min-w-[3.5rem] max-w-[5rem] text-muted" : "text-fg/90")}
                          style={{ textAlign: b.align[j] ?? undefined }}
                        >
                          {inline(r[j] ?? "", `${k}r${ri}c${j}`, ctx)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {tail}
          </Fragment>
        );
      }
      default:
        return null;
    }
  });
}

export const Markdown = memo(function Markdown({ text, className, trailing, linkClass }: { text: string; className?: string; trailing?: ReactNode; linkClass?: string }) {
  const blocks = parseBlocks(text ?? "");
  return (
    <div className={cn("break-words leading-relaxed", className)}>
      {blocks.length ? renderBlocks(blocks, "md", trailing ?? null, { linkClass }) : trailing}
    </div>
  );
});

/** Plain text for copy (strips the most common markdown marks). */
export function markdownToPlain(text: string): string {
  return text
    .replace(/```[\w-]*\n?/g, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)")
    .replace(/^#{1,6}\s+/gm, "")
    .trim();
}
